import React, {useCallback, useEffect} from 'react';
import {useAuthContext} from "../LauncherAuthContext";
import {Button, Divider, Form, message, Select, Switch, Typography} from "antd";
import {
    Account,
    PlayerProfileForm,
    fromPlayerProfileToForm,
    toPlayerProfileFromForm,
    PlayerExperienceLevel
} from "../types";
import {useTranslation} from "react-i18next";
import {MultiGameSelector} from "../../common/MultiGameSelector";
import {TriggerWarningSelector} from "../../common/TriggerWarningSelector";
import axios, {AxiosResponse} from "axios";
import {Label} from "../../../commons/translations/Label";
import {useNavigate} from "react-router";

export const ProfileForm: React.FC<{closeModal: () => void, hasTarget?: boolean, redirect?: string}> = (props) => {
    const { closeModal, hasTarget, redirect } = props;
    const { user, hasProfile, updateAccount } = useAuthContext();
    const [form] = Form.useForm<PlayerProfileForm>();
    const { t} = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        if (hasProfile() && user?.profile) {
            form.setFieldsValue(fromPlayerProfileToForm(user?.profile));
        }
    }, [hasProfile, user, form]);

    const updateProfile = useCallback((data: PlayerProfileForm) => {
        if (!user) {
            return;
        }
        const profileDto = toPlayerProfileFromForm(data);
        return axios.post<any, AxiosResponse<Account>>(`/api/launcher/users/account/${user.id}/player-profile`, profileDto)
            .then(r => r.data)
            .then((updated) => {
                message.success(t('profile.success.update'));
                updateAccount(updated);
                closeModal();
                if (hasTarget && redirect) {
                    navigate(redirect);
                }
            })
            .catch(() => message.error(t('profile.error.update')));
    },[user, t, updateAccount, closeModal, hasTarget, redirect, navigate]);

    return (
        <>
            <Form form={form} onFinish={updateProfile}>
                <Typography.Title level={3}><Label trl="profile.player.setup.prompt" /></Typography.Title>
                <Form.Item name={"experience"} label={<Label trl="profile.player.experience.label" />}>
                    <Select>
                        <Select.Option value={PlayerExperienceLevel.NEWBIE}>{t('experience.newbie')}</Select.Option>
                        <Select.Option value={PlayerExperienceLevel.BEGINNER}>{t('experience.beginner')}</Select.Option>
                        <Select.Option value={PlayerExperienceLevel.ADEPT}>{t('experience.adept')}</Select.Option>
                        <Select.Option value={PlayerExperienceLevel.MASTER}>{t('experience.master')}</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item name={"interests"} label={<Label trl="profile.player.interests.label" />}>
                    <MultiGameSelector />
                </Form.Item>
                <Form.Item name={"interestedInGm"} label={<Label trl="profile.player.isNarrator.label" />} valuePropName={"checked"} initialValue={false} >
                    <Switch />
                </Form.Item>
                <Divider />
                <Typography.Title level={5}><Label trl="profile.player.needs.prompt" /></Typography.Title>
                <Form.Item name={"photosensitivity"} label={<Label trl="profile.player.needs.photosensitivity" />} valuePropName={"checked"} initialValue={false} >
                    <Switch />
                </Form.Item>
                <Form.Item name={"visuallyImpaired"} label={<Label trl="profile.player.needs.visual" />} valuePropName={"checked"} initialValue={false} >
                    <Switch />
                </Form.Item>
                <Form.Item name={"hearingImpaired"} label={<Label trl="profile.player.needs.hearing" />} valuePropName={"checked"} initialValue={false} >
                    <Switch />
                </Form.Item>
                <Form.Item name={"needWheelchairAccess"} label={<Label trl="profile.player.needs.wheelchair" />} valuePropName={"checked"} initialValue={false} >
                    <Switch />
                </Form.Item>
                <Divider />
                <Typography.Title level={5}><Label trl="profile.player.warnings.prompt" /></Typography.Title>
                <Form.Item name={"contentWarnings"}>
                    <TriggerWarningSelector />
                </Form.Item>
                <p>
                    <Button style={{width: '100%'}} size={"large"} type="primary" onClick={form.submit}><Label trl="common.save" /></Button>
                </p>
                <p>
                    <Button style={{width: '100%'}} size={"large"} type="primary" danger onClick={closeModal}><Label trl="common.cancel" /></Button>
                </p>
            </Form>
        </>
    )
}
